import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import PDFImporter from '../../components/PDFImporter';
import ExameResultados from '../../components/ExameResultados';
import { gerarPdfExame } from '../../lib/pdfGenerator';
import '../../styles/adminForms.css';

const categoriaVazia = () => ({ titulo: '', exames: [''] });

function estadoInicial(exameEditando, alunaIdParam, alunas) {
  if (exameEditando) {
    return {
      ...exameEditando,
      titulo: exameEditando.titulo || '',
      categorias: exameEditando.categorias?.length > 0 ? exameEditando.categorias : [categoriaVazia()],
      orientacoesColeta: exameEditando.orientacoesColeta || '',
      obs: exameEditando.obs || '',
    };
  }
  return {
    alunaId: alunaIdParam || (alunas[0]?.id || ''),
    titulo: '',
    categorias: [categoriaVazia()],
    orientacoesColeta: '',
    obs: '',
  };
}

export default function CadastrarExame() {
  const { salvarExame, exames, deletarExame } = useData();
  const { alunas } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alunaIdParam = searchParams.get('aluna');
  const editandoId = searchParams.get('editar');
  const exameEditando = editandoId ? exames.find(e => e.id === editandoId) : null;
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState(() => estadoInicial(exameEditando, alunaIdParam, alunas));

  useEffect(() => {
    if (exameEditando) setForm(estadoInicial(exameEditando, alunaIdParam, alunas));
    else if (alunaIdParam) setForm(f => ({ ...f, alunaId: alunaIdParam }));
  }, [alunaIdParam, editandoId]);

  const alunaSel = alunas.find(a => a.id === form.alunaId);
  const upField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // --- categorias / exames ---
  const withCategoria = (ci, updater) => setForm(f => {
    const categorias = [...f.categorias];
    categorias[ci] = updater({ ...categorias[ci] });
    return { ...f, categorias };
  });
  const upCategoriaTitulo = (ci, v) => withCategoria(ci, c => ({ ...c, titulo: v }));
  const addCategoria = () => setForm(f => ({ ...f, categorias: [...f.categorias, categoriaVazia()] }));
  const remCategoria = (ci) => setForm(f => ({ ...f, categorias: f.categorias.filter((_, x) => x !== ci) }));

  const upExame = (ci, ei, v) => withCategoria(ci, c => { const exs = [...c.exames]; exs[ei] = v; return { ...c, exames: exs }; });
  const addExame = (ci) => withCategoria(ci, c => ({ ...c, exames: [...c.exames, ''] }));
  const remExame = (ci, ei) => withCategoria(ci, c => ({ ...c, exames: c.exames.filter((_, x) => x !== ei) }));

  function handlePDFImport(dados) {
    setForm(f => ({
      ...f,
      titulo: dados.titulo || f.titulo,
      categorias: dados.categorias?.length > 0 ? dados.categorias : f.categorias,
      orientacoesColeta: dados.orientacoesColeta || f.orientacoesColeta,
      obs: dados.obs || f.obs,
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    salvarExame({
      ...form,
      categorias: form.categorias
        .map(c => ({ ...c, exames: c.exames.filter(x => x.trim()) }))
        .filter(c => c.titulo.trim() || c.exames.length > 0),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    setForm(f => ({ ...f, titulo: '', categorias: [categoriaVazia()], orientacoesColeta: '', obs: '' }));
  }

  const examesDaAluna = exames.filter(e => e.alunaId === form.alunaId);

  return (
    <AdminLayout>
      <div className="af-page-header">
        <h1 className="af-title">{exameEditando ? 'Editar' : 'Cadastrar'} <em>Exame</em></h1>
        <button className="af-back-btn" onClick={() => alunaIdParam ? navigate('/admin/alunas') : navigate('/admin')}>← Voltar</button>
      </div>
      {alunaSel && <div className="af-aluna-chip"><span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--text)', color: '#FAF7F2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px' }}>{alunaSel.name.charAt(0)}</span>{alunaSel.name}</div>}
      <PDFImporter tipo="exame" onImport={handlePDFImport} />
      {saved && <div className="af-success">✓ Exame salvo com sucesso!</div>}
      <form className="af-form" onSubmit={handleSubmit}>
        <div className="af-card">
          <div className="af-card-title">Informações gerais</div>
          <div className="af-row">
            <div className="af-field"><label className="af-label">Aluna</label><select className="af-select" value={form.alunaId} onChange={e => upField('alunaId', e.target.value)}>{alunas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
            <div className="af-field"><label className="af-label">Título</label><input className="af-input" value={form.titulo} onChange={e => upField('titulo', e.target.value)} placeholder="Ex: Avaliação nutricional e metabólica" required /></div>
          </div>
        </div>

        <div className="af-card">
          <div className="af-card-title">Pedido de exame</div>
          {form.categorias.map((cat, ci) => (
            <div key={ci} className="af-sub-card">
              <div className="af-sub-num">{String(ci + 1).padStart(2, '0')}</div>
              {form.categorias.length > 1 && <button type="button" className="af-remove-btn" onClick={() => remCategoria(ci)}>✕</button>}
              <div className="af-field"><label className="af-label">Categoria</label><input className="af-input" value={cat.titulo} onChange={e => upCategoriaTitulo(ci, e.target.value)} placeholder="Ex: Avaliação do estado nutricional" /></div>
              <div className="af-sub-label">Exames</div>
              {cat.exames.map((ex, ei) => (
                <div key={ei} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input className="af-input" value={ex} onChange={e => upExame(ci, ei, e.target.value)} placeholder="Ex: Hemograma completo" />
                  <button type="button" className="af-mini-remove" onClick={() => remExame(ci, ei)}>✕</button>
                </div>
              ))}
              <button type="button" className="af-add-mini-btn" onClick={() => addExame(ci)}>+ exame</button>
            </div>
          ))}
          <button type="button" className="af-add-btn" onClick={addCategoria}>+ Adicionar categoria</button>
        </div>

        <div className="af-card">
          <div className="af-card-title">Orientações para coleta</div>
          <div className="af-field"><textarea className="af-textarea" value={form.orientacoesColeta} onChange={e => upField('orientacoesColeta', e.target.value)} placeholder="Ex: Realizar a coleta preferencialmente entre 7h e 10h da manhã. Jejum de 8 a 12 horas..." style={{ minHeight: '96px' }} /></div>
        </div>

        <div className="af-card">
          <div className="af-card-title">Observações</div>
          <div className="af-field"><textarea className="af-textarea" value={form.obs} onChange={e => upField('obs', e.target.value)} placeholder="Recado da equipe para a aluna sobre este pedido (aparece no app)..." style={{ minHeight: '64px' }} /></div>
        </div>

        <button type="submit" className="af-submit-btn">{exameEditando ? 'Salvar alterações' : 'Salvar pedido de exame'}</button>
      </form>
      {examesDaAluna.length > 0 && (
        <div style={{ marginTop: '32px', maxWidth: '720px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '12px' }}>Exames de {alunaSel?.name}</div>
          {examesDaAluna.map(ex => (
            <div key={ex.id} className="af-existing-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <div><div style={{ fontSize: '13px', fontWeight: 500 }}>{ex.titulo}</div><div style={{ fontSize: '11px', color: 'var(--text-2)' }}>{ex.categorias?.reduce((n, c) => n + c.exames.length, 0) || 0} exames{ex.resultados?.length > 0 && ` · ${ex.resultados.length} resultado(s) enviado(s)`}</div></div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button className="af-del-btn" style={{ color: 'var(--accent)', borderColor: 'rgba(137,91,64,0.3)', border: '1px solid', padding: '4px 10px', borderRadius: 'var(--r-btn)' }} onClick={() => gerarPdfExame(ex, alunaSel?.name || '')}>⬇ PDF</button>
                  <button className="af-del-btn" style={{ color: 'var(--accent)', borderColor: 'rgba(137,91,64,0.3)', border: '1px solid', padding: '4px 10px', borderRadius: 'var(--r-btn)' }} onClick={() => navigate(`/admin/exame/novo?aluna=${ex.alunaId}&editar=${ex.id}`)}>Editar</button>
                  <button className="af-del-btn" onClick={() => deletarExame(ex.id)}>Excluir</button>
                </div>
              </div>
              <ExameResultados exame={ex} alunaId={ex.alunaId} />
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
